import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { ROUTES } from "@/app/routes";
import { useGetApiAgents } from "@/lib/api/generated/agents/agents";
import {
  postApiSessionsBootstrap,
  postApiSessionsGatewayAccess,
  postApiSessionsGet,
  postApiSessionsFilesUpload,
  postApiSessionsFilesGet,
  postApiSessionsArtifactsGet,
  postApiSessionsThinking,
} from "@/lib/api/generated/sessions/sessions";
import type {
  SessionArtifactGetRequest,
  SessionBootstrapResponse,
  SessionFileGetRequest,
  SessionFileRecordDto,
  SessionFileUploadRequest,
  SessionFileUploadResponse,
  SessionGetResponse,
  SessionThinkingRequest,
  SessionThinkingResponse,
} from "@/lib/api/generated/control-api.schemas";
import { base64ToBlob, fileToBase64 } from "@/lib/file-codec";
import { SessionList, type SessionListEntry } from "./conversation-session-list";
import { ConversationMessageThread } from "./conversation-message-thread";
import { ConversationAssetsPanel } from "./conversation-assets-panel";
import { ConversationTaskPanel } from "./conversation-task-panel";
import { getLatestConversationIssue } from "./conversation-status.js";
import { useConversationsUiStore } from "@/stores/conversations-ui-store";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { runSessionGatewayTurn } from "../../../../../packages/shared-types/src/session-gateway-client.js";
import type {
  AgentThinkingDefault,
  MobileUiInboundAction,
} from "../../../../../packages/shared-types/src/index.js";
import { THINKING_DEFAULT_OPTIONS } from "../agents/agent-policy-utils";
import {
  sessionQueryKeys,
  upsertSessionSummary,
  useSessionLiveUpdates,
} from "./use-session-live-updates";

type SessionThinkingLevel = AgentThinkingDefault;

type SessionGatewaySendInput = {
  agentId: string;
  sessionId: string;
  text: string;
  action?: MobileUiInboundAction | null;
  thinkingLevel?: SessionThinkingLevel | null;
};

type ConversationActionReply = {
  label: string;
  replyText?: string;
  action?: MobileUiInboundAction;
};

function upsertSessionDetailFile(
  current: SessionGetResponse | undefined,
  file: SessionFileRecordDto,
  session: SessionGetResponse["session"],
): SessionGetResponse | undefined {
  if (!current) {
    return current;
  }

  const existingFiles = current.files.filter((entry) => entry.fileId !== file.fileId);
  return {
    ...current,
    session,
    files: [...existingFiles, file],
  };
}

function updateSessionDetailSummary(
  current: SessionGetResponse | undefined,
  session: SessionThinkingResponse["session"],
): SessionGetResponse | undefined {
  return current ? { ...current, session } : current;
}

export function ConversationsPage() {
  const { tenantId } = useTenantContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const runtimeBySessionId = useConversationsUiStore((state) => state.runtimeBySessionId);
  const downloadingId = useConversationsUiStore((state) => state.downloadingId);
  const applyRuntimeEvent = useConversationsUiStore((state) => state.applyRuntimeEvent);
  const clearRuntimeEvents = useConversationsUiStore((state) => state.clearRuntimeEvents);
  const setDownloadingId = useConversationsUiStore((state) => state.setDownloadingId);
  const setComposerText = useConversationsUiStore((state) => state.setComposerText);
  const clearComposerText = useConversationsUiStore((state) => state.clearComposerText);
  const pendingActionBySessionId = useConversationsUiStore(
    (state) => state.pendingActionBySessionId,
  );
  const clearPendingAction = useConversationsUiStore((state) => state.clearPendingAction);
  const composerTextBySessionId = useConversationsUiStore((state) => state.composerTextBySessionId);

  useDocumentTitle("Conversations");

  const agentsQuery = useGetApiAgents({ query: { enabled: Boolean(tenantId) } });
  const routeSessionId = searchParams.get("session") || undefined;

  const bootstrapQuery = useQuery({
    queryKey: sessionQueryKeys.bootstrap(tenantId!),
    queryFn: async () => await postApiSessionsBootstrap({}),
    enabled: Boolean(tenantId),
    staleTime: 5 * 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const sessionEntries = useMemo<SessionListEntry[]>(() => {
    const bootstrap = bootstrapQuery.data;
    if (!bootstrap) {
      return [];
    }

    const sessionsById = new Map(
      bootstrap.sessions.map((session) => [session.sessionId, session]),
    );

    return bootstrap.visibleAgents.map((agent) => {
      const existing = sessionsById.get(agent.sessionId);
      const runtimeEvents = runtimeBySessionId[agent.sessionId]?.events ?? [];
      return {
        sessionId: agent.sessionId,
        agentId: agent.agentId,
        title: existing?.title || agent.name,
        subtitle: existing?.lastMessageText?.trim() || agent.description || agent.agentId,
        updatedAt: existing?.lastMessageAt || existing?.updatedAt,
        thinkingLevel: (existing?.thinkingLevel ?? null) as SessionThinkingLevel | null,
        thinkingDefault: (agent.thinkingDefault ?? null) as SessionThinkingLevel | null,
        allowUserThinkingOverride: agent.allowUserThinkingOverride,
        hasMessages: (existing?.messageCount ?? 0) > 0,
        hasLiveRuntimeActivity: isRuntimeSessionActive(runtimeEvents),
      };
    });
  }, [bootstrapQuery.data, runtimeBySessionId]);

  const liveSessionIds = useMemo(
    () => bootstrapQuery.data?.visibleAgents.map((agent) => agent.sessionId) ?? [],
    [bootstrapQuery.data?.visibleAgents],
  );

  const selectedConversationId = routeSessionId || sessionEntries[0]?.sessionId || undefined;
  const composerText = selectedConversationId ? composerTextBySessionId[selectedConversationId] ?? "" : "";
  const pendingAction = selectedConversationId
    ? pendingActionBySessionId[selectedConversationId]
    : undefined;
  const pendingAutomationResultName =
    pendingAction?.target?.kind === "automation_result"
      ? pendingAction.target.label ?? pendingAction.target.id
      : null;

  useEffect(() => {
    if (!searchParams.get("session") && sessionEntries[0]?.sessionId) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set("session", sessionEntries[0].sessionId);
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [searchParams, sessionEntries, setSearchParams]);

  const sessionQuery = useQuery({
    queryKey: sessionQueryKeys.detail(tenantId!, selectedConversationId ?? "none"),
    queryFn: async () => await postApiSessionsGet({ sessionId: selectedConversationId! }),
    enabled: Boolean(tenantId && selectedConversationId),
    refetchOnWindowFocus: false,
  });

  const sendMutation = useMutation({
    mutationFn: async (request: SessionGatewaySendInput) => {
      const access = await postApiSessionsGatewayAccess({
        agentId: request.agentId,
        sessionId: request.sessionId,
      });
      queryClient.setQueryData<SessionBootstrapResponse | undefined>(
        sessionQueryKeys.bootstrap(tenantId!),
        (current) => upsertSessionSummary(current, access.session),
      );
      await runSessionGatewayTurn({
        access,
        text: request.text,
        action: request.action,
        thinkLevel: request.thinkingLevel,
        client: {
          id: "webchat-ui",
          displayName: "Agentiq Web",
          version: "agentiq-web",
          platform: "web",
          mode: "webchat",
        },
        onRuntimeEvent: applyRuntimeEvent,
      });
      return access;
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (request: SessionFileUploadRequest) =>
      await postApiSessionsFilesUpload(request) as SessionFileUploadResponse,
    onSuccess: async (result) => {
      queryClient.setQueryData<SessionBootstrapResponse | undefined>(
        sessionQueryKeys.bootstrap(tenantId!),
        (current) => upsertSessionSummary(current, result.session),
      );
      queryClient.setQueryData<SessionGetResponse | undefined>(
        sessionQueryKeys.detail(tenantId!, result.session.sessionId),
        (current) => upsertSessionDetailFile(current, result.file, result.session),
      );
    },
  });

  const thinkingMutation = useMutation({
    mutationFn: async (request: SessionThinkingRequest) => await postApiSessionsThinking(request),
    onSuccess: (result) => {
      queryClient.setQueryData<SessionBootstrapResponse | undefined>(
        sessionQueryKeys.bootstrap(tenantId!),
        (current) => upsertSessionSummary(current, result.session),
      );
      queryClient.setQueryData<SessionGetResponse | undefined>(
        sessionQueryKeys.detail(tenantId!, result.session.sessionId),
        (current) => updateSessionDetailSummary(current, result.session),
      );
    },
  });

  const selectedSession = sessionEntries.find((entry) => entry.sessionId === selectedConversationId) || null;
  const session = sessionQuery.data;
  const selectedThinkingLevel =
    ((session?.session.thinkingLevel ?? selectedSession?.thinkingLevel ?? null) as SessionThinkingLevel | null);
  const selectedThinkingDefault =
    ((selectedSession?.thinkingDefault ?? null) as SessionThinkingLevel | null);
  const selectedThinkingValue = selectedThinkingLevel ?? "agent-default";
  const latestIssue = getLatestConversationIssue(session?.messages ?? []);
  const runtimeEvents = selectedConversationId ? runtimeBySessionId[selectedConversationId]?.events ?? [] : [];

  useSessionLiveUpdates({
    tenantId,
    sessionIds: liveSessionIds,
    onRuntimeEvent: applyRuntimeEvent,
    onAssistantMessage: clearRuntimeEvents,
  });

  async function handleSend() {
    const text = composerText.trim();
    if (!selectedSession || !text) {
      return;
    }

    try {
      clearRuntimeEvents(selectedSession.sessionId);
      await sendMutation.mutateAsync({
        agentId: selectedSession.agentId,
        sessionId: selectedSession.sessionId,
        text,
        thinkingLevel: selectedThinkingLevel,
        ...(pendingAction ? { action: pendingAction } : {}),
      });
      clearComposerText(selectedSession.sessionId);
      clearPendingAction(selectedSession.sessionId);
    } catch (error) {
      setComposerText(selectedSession.sessionId, text);
      toast.error(error instanceof Error ? error.message : "Failed to send message.");
    }
  }

  async function handleActionReply(payload: ConversationActionReply) {
    if (!selectedSession) {
      return;
    }

    const text = payload.replyText?.trim() || payload.label.trim();
    if (!text) {
      return;
    }

    try {
      clearRuntimeEvents(selectedSession.sessionId);
      await sendMutation.mutateAsync({
        agentId: selectedSession.agentId,
        sessionId: selectedSession.sessionId,
        text,
        thinkingLevel: selectedThinkingLevel,
        ...(payload.action ? { action: payload.action } : {}),
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to send action.");
    }
  }

  async function handleUpload(files: FileList | null) {
    if (!files?.length || !selectedSession) {
      return;
    }

    try {
      for (const file of Array.from(files)) {
        await uploadMutation.mutateAsync({
          agentId: selectedSession.agentId,
          sessionId: selectedSession.sessionId,
          filename: file.name,
          mimeType: file.type || undefined,
          contentBase64: await fileToBase64(file),
        });
      }
      toast.success(files.length === 1 ? "File attached." : `${files.length} files attached.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to upload file.");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }

  async function handleThinkingChange(value: string) {
    if (!selectedSession) {
      return;
    }
    const nextThinkingLevel = value === "agent-default" ? null : (value as SessionThinkingLevel);
    try {
      await thinkingMutation.mutateAsync({
        agentId: selectedSession.agentId,
        sessionId: selectedSession.sessionId,
        thinkingLevel: nextThinkingLevel,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update reasoning level.");
    }
  }

  async function handleDownloadFile(fileId: string) {
    if (!selectedConversationId || downloadingId) {
      return;
    }

    setDownloadingId(fileId);
    try {
      const response = await postApiSessionsFilesGet({
        sessionId: selectedConversationId,
        fileId,
      } as SessionFileGetRequest);
      downloadBlob(response.file.filename, base64ToBlob(response.contentBase64, response.file.mimeType));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to download file.");
    } finally {
      setDownloadingId(null);
    }
  }

  async function handleDownloadArtifact(artifactId: string) {
    if (!selectedConversationId || downloadingId) {
      return;
    }

    setDownloadingId(artifactId);
    try {
      const response = await postApiSessionsArtifactsGet({
        sessionId: selectedConversationId,
        artifactId,
      } as SessionArtifactGetRequest);
      downloadBlob(response.artifact.filename, base64ToBlob(response.contentBase64, response.artifact.mimeType));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to download artifact.");
    } finally {
      setDownloadingId(null);
    }
  }

  if (agentsQuery.isLoading || bootstrapQuery.isLoading) {
    return <ConversationLoadingState />;
  }

  if ((agentsQuery.data?.length ?? 0) === 0) {
    return (
      <EmptyState
        title="No agents yet"
        description="Conversations run through an agent. Create one first, then use this workspace like ChatGPT with reusable session files."
        action={{
          label: "Create first agent",
          onClick: () => navigate(ROUTES.tenantAgentNew(tenantId!)),
        }}
      />
    );
  }

  if (bootstrapQuery.error) {
    return (
      <EmptyState
        title="Conversation workspace unavailable"
        description={
          bootstrapQuery.error instanceof Error
            ? bootstrapQuery.error.message
            : "Failed to load sessions."
        }
      />
    );
  }

  return (
    <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
      <Card className="h-[calc(100vh-11rem)] overflow-hidden">
        <CardHeader className="border-b border-border">
          <CardTitle className="text-base">Conversations</CardTitle>
        </CardHeader>
        <CardContent className="h-[calc(100%-4.25rem)] overflow-y-auto p-3">
          <SessionList
            sessions={sessionEntries}
            selectedConversationId={selectedConversationId}
            onSelect={(sessionId) => {
              const nextSearchParams = new URLSearchParams(searchParams);
              nextSearchParams.set("session", sessionId);
              setSearchParams(nextSearchParams, { replace: true });
            }}
          />
        </CardContent>
      </Card>

      <div className="grid gap-4">
        <Card className="overflow-hidden">
          <CardHeader className="border-b border-border">
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base">
                  {session?.session.title || selectedSession?.title || "Conversation"}
                </CardTitle>
                <div className="mt-1 text-sm" style={{ color: "var(--muted-foreground)" }}>
                  {selectedSession?.subtitle || "Ask the agent to work with the files attached to this session."}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {selectedSession?.allowUserThinkingOverride ? (
                  <SessionThinkingSelect
                    value={selectedThinkingValue}
                    defaultLevel={selectedThinkingDefault}
                    disabled={thinkingMutation.isPending}
                    onChange={(value) => void handleThinkingChange(value)}
                  />
                ) : null}
                <div className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                  {session?.session.agentName || selectedSession?.agentId}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="grid gap-4 p-4">
            <div className="max-h-[45vh] overflow-y-auto">
              <ConversationMessageThread
                messages={session?.messages ?? []}
                runtimeEvents={runtimeEvents}
                onActionReply={(payload) => void handleActionReply(payload)}
              />
            </div>

            <div className="grid gap-3">
              {pendingAutomationResultName ? (
                <div
                  className="rounded-md border px-3 py-3 text-sm"
                  style={{
                    borderColor: "color-mix(in srgb, var(--brand) 25%, var(--border))",
                    background: "color-mix(in srgb, var(--brand) 10%, transparent)",
                    color: "var(--text-strong)",
                  }}
                >
                  The next message will include the attached automation result reference for {pendingAutomationResultName}.
                </div>
              ) : null}
              {latestIssue ? (
                <div
                  className="rounded-md border px-3 py-3 text-sm"
                  style={{
                    borderColor: "color-mix(in srgb, var(--danger) 35%, var(--border))",
                    background: "color-mix(in srgb, var(--danger) 10%, transparent)",
                    color: "var(--text-strong)",
                  }}
                >
                  <div
                    className="mb-1 text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: "var(--danger)" }}
                  >
                    Latest runtime issue
                  </div>
                  <div className="whitespace-pre-wrap leading-6">{latestIssue}</div>
                </div>
              ) : null}
              <Textarea
                value={composerText}
                onChange={(event) => {
                  if (!selectedConversationId) {
                    return;
                  }
                  setComposerText(selectedConversationId, event.target.value);
                }}
                placeholder="Ask the agent to analyze, compare, rewrite, or generate files from this session..."
                rows={5}
              />
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                  Session files stay attached to this session across turns.
                </div>
                <Button
                  onClick={() => void handleSend()}
                  disabled={!composerText.trim() || !selectedSession || sendMutation.isPending}
                >
                  Send
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <ConversationAssetsPanel
          files={session?.files ?? []}
          artifacts={session?.artifacts ?? []}
          uploading={uploadMutation.isPending}
          onUploadClick={() => fileInputRef.current?.click()}
          onDownloadFile={(fileId) => void handleDownloadFile(fileId)}
          onDownloadArtifact={(artifactId) => void handleDownloadArtifact(artifactId)}
        />

        <ConversationTaskPanel tenantId={tenantId!} sessionKey={selectedConversationId} />

        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(event) => void handleUpload(event.target.files)}
        />
      </div>
    </div>
  );
}

function isRuntimeSessionActive(
  events: Array<{ stream: string; data: Record<string, unknown> }>,
): boolean {
  const latestEvent = events.at(-1);
  if (!latestEvent) {
    return false;
  }
  if (latestEvent.stream !== "lifecycle") {
    return true;
  }
  const phase = typeof latestEvent.data.phase === "string" ? latestEvent.data.phase : undefined;
  return phase !== "end" && phase !== "error";
}

function ConversationLoadingState() {
  return (
    <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
      <div className="h-[calc(100vh-11rem)] rounded-md border" style={{ borderColor: "var(--border)" }} />
      <div className="grid gap-4">
        <div className="h-80 rounded-md border" style={{ borderColor: "var(--border)" }} />
        <div className="h-56 rounded-md border" style={{ borderColor: "var(--border)" }} />
      </div>
    </div>
  );
}

function SessionThinkingSelect({
  value,
  defaultLevel,
  disabled,
  onChange,
}: {
  value: SessionThinkingLevel | "agent-default";
  defaultLevel: SessionThinkingLevel | null;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const defaultLabel = defaultLevel ? labelForThinkingLevel(defaultLevel) : "agent default";
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs" style={{ color: "var(--muted-foreground)" }}>
        Reasoning
      </span>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="h-8 w-[170px] bg-white text-xs">
          <SelectValue placeholder="Reasoning" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="agent-default">Agent default ({defaultLabel})</SelectItem>
          {THINKING_DEFAULT_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function labelForThinkingLevel(value: SessionThinkingLevel) {
  return THINKING_DEFAULT_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

function downloadBlob(filename: string, blob: Blob) {
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(href);
}
